/**
 * Club Setup — first-run setup for club owners.
 *
 * Shown after purchase when the org is "incomplete":
 *   - name === 'Your Club' (placeholder), OR
 *   - organizations.setup_complete IS NOT TRUE
 *
 * Steps:
 *   1. Org name input
 *   2. Org type picker (gym / school / team / other)
 *   → Save → update organizations row → navigate to home (or club dashboard when live)
 *
 * Minimal scope: no logo upload, no accent picker, no team management.
 *
 * Doctrine: DUEL_CLUB_MODE_ARCHITECTURE.md
 *   - Uses `organizations` table only — no `gyms` dependency.
 */

import { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  ScrollView,
  Pressable,
  SafeAreaView,
  ActivityIndicator,
  Alert,
  Platform,
  TouchableOpacity,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { resolveClubOwnerOrg } from '@/lib/orgProvisioning';
import { TYPO } from '@/constants/typography';
import type { OrgType } from '@/types/database';
import { ChevronLeft } from 'lucide-react-native';

// ─── Types ────────────────────────────────────────────────────────────────────

interface OrgTypeOption {
  value: OrgType;
  label: string;
  emoji: string;
}

const ORG_TYPE_OPTIONS: OrgTypeOption[] = [
  { value: 'gym', label: 'Gym / Fitness Studio', emoji: '🏋️' },
  { value: 'school', label: 'School / Athletic Department', emoji: '🏫' },
  { value: 'team', label: 'Sports Team / Club', emoji: '⚽' },
  { value: 'company', label: 'Other Organization', emoji: '🏢' },
];

// ─── Screen ───────────────────────────────────────────────────────────────────

export default function ClubSetup() {
  const router = useRouter();
  const { theme } = useTheme();
  const { session } = useAuth();
  const c = theme.colors;

  const userId = session?.user?.id ?? '';

  const [orgId, setOrgId] = useState<string | null>(null);
  const [orgName, setOrgName] = useState('');
  const [orgType, setOrgType] = useState<OrgType>('gym');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load existing org on mount
  useEffect(() => {
    if (!userId) return;
    resolveClubOwnerOrg(userId).then((id) => {
      if (id) {
        setOrgId(id);
        // Prefill current values
        supabase
          .from('organizations')
          .select('name, org_type')
          .eq('id', id)
          .single()
          .then(({ data }) => {
            if (data) {
              setOrgName(data.name === 'Your Club' ? '' : data.name);
              setOrgType((data.org_type as OrgType) ?? 'gym');
            }
          });
      }
      setLoading(false);
    });
  }, [userId]);

  const handleSave = async () => {
    const trimmedName = orgName.trim();
    if (!trimmedName) {
      setError('Please enter a name for your organization.');
      return;
    }
    if (!orgId) {
      setError('No organization found. Please contact support.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('organizations')
        .update({
          name: trimmedName,
          org_type: orgType,
          setup_complete: true,
        })
        .eq('id', orgId);

      if (updateError) throw updateError;

      // Navigate to Club Home — primary post-setup landing (Phase 3)
      if (orgId) {
        router.replace({
          pathname: '/club-home',
          params: { orgId },
        } as any);
      } else if (Platform.OS !== 'web') {
        router.replace('/' as any);
      } else {
        router.replace('/');
      }
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: c.background, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator color={c.accent} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: c.background }}>
      {/* Header */}
      <View style={{ padding: 16, paddingBottom: 0 }}>
        <TouchableOpacity
          onPress={() => router.back()}
          style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12, alignSelf: 'flex-start' }}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <ChevronLeft size={20} color={c.accent} />
          <Text style={{ color: c.accent, fontSize: 14, fontFamily: TYPO.body.regular }}>Back</Text>
        </TouchableOpacity>
        <Text style={{ fontSize: 24, fontFamily: TYPO.heading.bold, color: c.text, marginBottom: 4 }}>
          Set Up Your Club
        </Text>
        <Text style={{ fontSize: 14, color: c.textSecondary, lineHeight: 20 }}>
          Tell us a bit about your organization to get started.
        </Text>
      </View>

      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 48 }}>

        {/* Org Name */}
        <View style={{ marginBottom: 28 }}>
          <Text style={{ fontSize: 13, fontFamily: TYPO.body.semibold, color: c.textSecondary, marginBottom: 8, letterSpacing: 0.5, textTransform: 'uppercase' }}>
            Organization Name
          </Text>
          <TextInput
            value={orgName}
            onChangeText={setOrgName}
            placeholder="e.g. Iron Peak Gym"
            placeholderTextColor={c.textTertiary}
            style={{
              backgroundColor: c.surface,
              borderWidth: 1.5,
              borderColor: c.border,
              borderRadius: 4,
              padding: 14,
              fontSize: 16,
              color: c.text,
              fontFamily: TYPO.body.regular,
            }}
            maxLength={80}
            autoCapitalize="words"
            returnKeyType="done"
          />
        </View>

        {/* Org Type */}
        <View style={{ marginBottom: 32 }}>
          <Text style={{ fontSize: 13, fontFamily: TYPO.body.semibold, color: c.textSecondary, marginBottom: 8, letterSpacing: 0.5, textTransform: 'uppercase' }}>
            Organization Type
          </Text>
          {ORG_TYPE_OPTIONS.map((opt) => (
            <Pressable
              key={opt.value}
              onPress={() => setOrgType(opt.value)}
              style={{
                backgroundColor: orgType === opt.value ? c.accent + '20' : c.surface,
                borderWidth: 1.5,
                borderColor: orgType === opt.value ? c.accent : c.border,
                borderRadius: 4,
                padding: 14,
                marginBottom: 8,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <Text style={{ fontSize: 20 }}>{opt.emoji}</Text>
              <Text style={{
                fontSize: 15,
                color: c.text,
                fontFamily: orgType === opt.value ? TYPO.body.semibold : TYPO.body.regular,
              }}>
                {opt.label}
              </Text>
            </Pressable>
          ))}
        </View>

        {/* Error */}
        {error ? (
          <Text style={{ color: '#ef4444', fontSize: 14, marginBottom: 16 }}>{error}</Text>
        ) : null}

        {/* Save */}
        <Pressable
          onPress={handleSave}
          disabled={saving}
          style={{
            backgroundColor: c.accent,
            borderRadius: 4,
            padding: 16,
            alignItems: 'center',
          }}
        >
          {saving ? (
            <ActivityIndicator color={c.accentForeground} />
          ) : (
            <Text style={{ color: c.accentForeground, fontFamily: TYPO.heading.bold, fontSize: 16 }}>
              Save &amp; Continue
            </Text>
          )}
        </Pressable>

        <Text style={{ color: c.textTertiary, fontSize: 12, textAlign: 'center', marginTop: 16 }}>
          You can update these details later in your club settings.
        </Text>

        {/* Quick links for owners who already completed setup */}
        {orgId ? (
          <View style={{ marginTop: 16, gap: 10 }}>
            <Pressable
              onPress={() =>
                router.push({
                  pathname: '/club-home',
                  params: { orgId },
                } as any)
              }
              style={{ alignItems: 'center' }}
            >
              <Text style={{ color: c.accent, fontSize: 14, fontFamily: TYPO.body.regular }}>
                🏠 Club Home →
              </Text>
            </Pressable>
            <Pressable
              onPress={() =>
                router.push({
                  pathname: '/org-roster',
                  params: { orgId },
                } as any)
              }
              style={{ alignItems: 'center' }}
            >
              <Text style={{ color: c.accent, fontSize: 14, fontFamily: TYPO.body.regular }}>
                Manage Roster →
              </Text>
            </Pressable>
          </View>
        ) : null}
      </ScrollView>
    </SafeAreaView>
  );
}
